/**
 * Everything the sidebar links to, as data.
 *
 * The sidebar, the breadcrumb in the top bar and the page title all read this
 * one list, so a page added here gets a link, a crumb and an active state
 * without anyone remembering to touch three components. Adding a whole new area
 * (the "Work" group with assignments and reviews, say) is one more entry in
 * `NAV_GROUPS` with its own `visible` rule.
 *
 * Visibility here is presentation only. A link hidden from a role is also
 * refused by the route guard in app.tsx and by the API, which are what actually
 * keep the page private; hiding it just spares people a link that goes nowhere.
 */
import {
  ActivityIcon,
  BotIcon,
  BotMessageSquareIcon,
  BrainIcon,
  CircleDollarSignIcon,
  ClapperboardIcon,
  FlameIcon,
  FolderOpenIcon,
  KanbanIcon,
  LayoutGridIcon,
  ImagesIcon,
  KeyRoundIcon,
  LanguagesIcon,
  LightbulbIcon,
  LayersIcon,
  MessagesSquareIcon,
  NotebookPenIcon,
  ShapesIcon,
  SettingsIcon,
  SparklesIcon,
  TrophyIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";
import { canUsePipeline, isAdminRole, type Role, type StudioAccess } from "@scandi/shared";
import type { Catalog } from "@/i18n/locales/en";

/**
 * A `nav.*` key, or a feature's own name for its page (the AI spend page keeps its words in its catalog module;
 * the studio's group and pages keep theirs in `studio.shell.nav`).
 */
export type NavLabelKey =
  | `nav.${keyof Catalog["nav"]}`
  | "generation.admin.nav"
  | "generation.library.nav"
  | "ideas.nav"
  | "promptChat.nav"
  | "notify.nav"
  | "translations.nav"
  | "studio.shell.nav.group"
  | "studio.shell.nav.home"
  | "studio.shell.nav.chat"
  | "studio.shell.nav.jobs"
  | "studio.shell.nav.admin";

/**
 * What a reader has beyond their role: the studio, which an admin can open to a
 * user or editor as the "Script chat" add-on (`useSession().studio`).
 */
export interface NavAccess {
  studio: StudioAccess | null;
}

/** For a reader whose add-ons are not known: the role alone decides. */
const ROLE_ONLY: NavAccess = { studio: null };

/** Extra state an item can show beside its label. Resolved by the sidebar. */
export type NavBadge = "pendingUsers" | "translationsLive" | "agentJobsLive";

export interface NavItem {
  /** Stable id, used as the React key. */
  id: string;
  to: string;
  label: NavLabelKey;
  icon: LucideIcon;
  /**
   * Whether this item is the one the current page belongs to. Defaults to an
   * exact match; items that own detail pages (one editor, one conversation)
   * claim those paths too, so the sidebar still shows where the reader is.
   */
  matches?: (pathname: string) => boolean;
  visible?: (role: Role, access: NavAccess) => boolean;
  badge?: NavBadge;
}

export interface NavGroup {
  id: string;
  label: NavLabelKey;
  items: NavItem[];
  visible?: (role: Role, access: NavAccess) => boolean;
}

const adminOnly = (role: Role) => isAdminRole(role);

/** The deck register: an admin's. The matching guard is in app.tsx. */
const authorOnly = (role: Role) => isAdminRole(role);

/** Whoever looks across editors rather than at one desk. */
const acrossEditors = (role: Role) => isAdminRole(role) || role === "strategist";

/** Anyone with some of the studio: strategists and admins, and the "Script chat" add-on. */
const anyStudio = (_role: Role, access: NavAccess) => access.studio !== null;

/** The whole studio, not just the chat: strategists and admins. */
const wholeStudio = (_role: Role, access: NavAccess) => access.studio === "full";

/**
 * A concept and the pages that hang off it: the brief (`/pipeline/b/:ref`),
 * its presentation editor (`/presentacion`, the script `/guion`, a line
 * `/l/:lineKey`, a shot `/l/:lineKey/s/:shotKey`) and its deck (`/deck`).
 * All of them belong under the list the concept was opened from.
 */
const CONCEPT_PATH = /^\/pipeline\/b\/[^/]+(?:\/(?:presentacion(?:\/.*)?|deck))?\/?$/;
const isConceptPath = (path: string) => CONCEPT_PATH.test(path);

/**
 * The studio's two halves. One chat (`/estudio/guion/:id`) belongs under the
 * chats, and the job form and one job under the jobs. The front page
 * (`/estudio`) is its own entry, "Overview".
 */
const isStudioChatPath = (path: string) => path === "/estudio/guion" || path.startsWith("/estudio/guion/");
const isStudioJobsPath = (path: string) => path === "/estudio/trabajos" || path.startsWith("/estudio/trabajos/");

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "performance",
    label: "nav.groupPerformance",
    items: [
      {
        id: "leaderboard",
        to: "/leaderboard",
        label: "nav.leaderboard",
        icon: TrophyIcon,
        // One editor's page is opened from the board, so it belongs under it.
        matches: (path) => path === "/leaderboard" || path.startsWith("/editors/"),
      },
      {
        id: "top-videos",
        to: "/videos",
        label: "nav.topVideos",
        icon: FlameIcon,
        matches: (path) => path === "/videos" || path.startsWith("/creatives/"),
      },
    ],
  },
  {
    id: "pipeline",
    label: "nav.groupPipeline",
    // A results-only `user` (a social media manager, say) sees the performance
    // pages and the coach, and nothing of the creative pipeline.
    visible: canUsePipeline,
    items: [
      {
        id: "assignments",
        to: "/pipeline/mi-trabajo",
        label: "nav.myWork",
        icon: KanbanIcon,
        // One concept is opened from this list, so it belongs under it.
        matches: (path) => path === "/pipeline/mi-trabajo" || isConceptPath(path),
        // An editor's working page. Everyone else reads "Conceptos", which
        // answers the same question across the whole team rather than one desk.
        visible: (role) => role === "editor",
      },
      {
        id: "concepts",
        to: "/pipeline/conceptos",
        label: "nav.concepts",
        icon: LayersIcon,
        // Every concept of every editor. A concept opened from it belongs under
        // it - for the roles that have it; an editor's is claimed by "Mi
        // trabajo" above, which `findActive` reaches first for them.
        matches: (path) => path === "/pipeline/conceptos" || isConceptPath(path),
        visible: acrossEditors,
      },
      {
        id: "artifacts",
        to: "/artefactos",
        label: "nav.artifacts",
        icon: ShapesIcon,
        // Every published deck, report and showcase (the deck register grew into it): an admin's. An
        // editor reads the one deck on their own concept and never needs the list of everybody's.
        matches: (path) => path === "/artefactos" || path.startsWith("/artefactos/"),
        visible: authorOnly,
      },
      {
        id: "resources",
        to: "/recursos",
        label: "nav.resources",
        icon: FolderOpenIcon,
        // Pipeline material: not for a results-only account.
        visible: canUsePipeline,
      },
      {
        // "Suggest a concept": anyone in the pipeline suggests, strategists
        // and admins review. One idea's page belongs under it.
        id: "ideas",
        to: "/ideas",
        label: "ideas.nav",
        icon: LightbulbIcon,
        matches: (path) => path === "/ideas" || path.startsWith("/ideas/"),
        visible: canUsePipeline,
      },
    ],
  },
  {
    // The cloud agents (scandi-cloud-agents): the scriptwriter's chats and the
    // ad jobs they hand on. Strategists' work; a user or editor sees only the
    // chat, and only with the "Script chat" add-on. The routes and the API
    // refuse the rest too.
    id: "studio",
    label: "studio.shell.nav.group",
    visible: anyStudio,
    items: [
      {
        // The front page: the two ways in, recent chats, ad jobs in progress.
        id: "studio-home",
        to: "/estudio",
        label: "studio.shell.nav.home",
        icon: LayoutGridIcon,
        visible: wholeStudio,
      },
      {
        id: "studio-chat",
        to: "/estudio/guion",
        label: "studio.shell.nav.chat",
        icon: NotebookPenIcon,
        matches: isStudioChatPath,
      },
      {
        // The count is the reader's ad jobs in progress (`useAgentJobsLiveCount`).
        id: "studio-jobs",
        to: "/estudio/trabajos",
        label: "studio.shell.nav.jobs",
        icon: ClapperboardIcon,
        matches: isStudioJobsPath,
        badge: "agentJobsLive",
        visible: wholeStudio,
      },
    ],
  },
  {
    id: "tools",
    label: "nav.groupTools",
    items: [
      { id: "coach", to: "/coach", label: "nav.aiCoach", icon: SparklesIcon },
      {
        // The team's product pictures and voices for AI generation. Its two
        // tabs are two addresses; both belong to this one entry.
        id: "library",
        to: "/biblioteca",
        label: "generation.library.nav",
        icon: ImagesIcon,
        matches: (path) => path === "/biblioteca" || path.startsWith("/biblioteca/"),
        // Editors generate too, so they add product pictures; the voices tab stays the others'.
        visible: (role) => acrossEditors(role) || role === "editor",
      },
      {
        // The prompt chat's team memory and its change log (its settings are in Settings).
        id: "prompt-chat",
        to: "/admin/prompt-chat",
        label: "promptChat.nav",
        icon: BrainIcon,
        visible: acrossEditors,
      },
    ],
  },
  {
    id: "admin",
    label: "nav.groupAdmin",
    visible: adminOnly,
    items: [
      {
        id: "users",
        to: "/admin/users",
        label: "nav.users",
        icon: UsersIcon,
        badge: "pendingUsers",
      },
      {
        id: "activity",
        to: "/admin/actividad",
        label: "nav.activity",
        icon: ActivityIcon,
      },
      {
        // Every dubbed and subtitled ad, by concept and hook; the count is the cloud worker's jobs in flight.
        id: "translations",
        to: "/admin/traducciones",
        label: "translations.nav",
        icon: LanguagesIcon,
        badge: "translationsLive",
      },
      {
        // The cloud agents: the audit log, captured knowledge, health (their settings are in Settings).
        id: "admin-agents",
        to: "/admin/agentes",
        label: "studio.shell.nav.admin",
        icon: BotIcon,
      },
      {
        // What the team spends on AI (its caps and prices are in Settings).
        id: "ai-spend",
        to: "/admin/gasto",
        label: "generation.admin.nav",
        icon: CircleDollarSignIcon,
      },
      {
        id: "api-keys",
        to: "/admin/api-keys",
        label: "nav.apiKeys",
        icon: KeyRoundIcon,
      },
      {
        id: "conversations",
        to: "/coach/sessions",
        label: "nav.conversations",
        icon: MessagesSquareIcon,
        matches: (path) => path.startsWith("/coach/sessions"),
      },
    ],
  },
];

/**
 * Settings: every setting on the platform, one hub (`features/settings`). Its
 * own entry at the foot of the sidebar, for everyone - which sections a reader
 * sees is the hub's to decide (their own account and notifications at least).
 */
export const SETTINGS_ITEM: NavItem = {
  id: "settings",
  to: "/settings",
  label: "nav.settings",
  icon: SettingsIcon,
  matches: (path) => path.startsWith("/settings"),
};

/**
 * The admins' own chat agent (research, data, ad batches, artifacts). Drawn in
 * the sidebar's "Apps" group beside the separate apps, for real admins only;
 * unlike those it is a page of this build, so an in-app link.
 */
export const MASTER_ITEM: NavItem = {
  id: "master-ai",
  to: "/master",
  label: "nav.masterAi",
  icon: BotMessageSquareIcon,
  matches: (path) => path === "/master" || path.startsWith("/master/"),
};

/**
 * Pages reached from somewhere other than the sidebar's groups that still need
 * a crumb and a title.
 */
const OTHER_PAGES: Array<{ group: NavGroup | null; item: NavItem }> = [
  { group: null, item: SETTINGS_ITEM },
  { group: null, item: MASTER_ITEM },
];

export const isItemActive = (item: NavItem, pathname: string): boolean =>
  item.matches ? item.matches(pathname) : pathname === item.to;

export function visibleGroups(role: Role, access: NavAccess = ROLE_ONLY): NavGroup[] {
  return NAV_GROUPS.filter((group) => group.visible?.(role, access) ?? true)
    .map((group) => ({ ...group, items: group.items.filter((item) => item.visible?.(role, access) ?? true) }))
    .filter((group) => group.items.length > 0);
}

/**
 * The group and item the current page belongs to, for the breadcrumb.
 *
 * Asked with the reader's role when there is one: a concept's page is claimed
 * by "Mi trabajo" for an editor and by "Conceptos" for everyone else, and the
 * crumb should name the list that reader actually has.
 */
export function findActive(
  pathname: string,
  role?: Role | null,
  access: NavAccess = ROLE_ONLY,
): { group: NavGroup | null; item: NavItem } | null {
  // What the reader can see first, then everything: a page reached by a link
  // the sidebar does not show them still deserves a crumb.
  for (const groups of role ? [visibleGroups(role, access), NAV_GROUPS] : [NAV_GROUPS]) {
    for (const group of groups) {
      const item = group.items.find((candidate) => isItemActive(candidate, pathname));
      if (item) return { group, item };
    }
  }
  return OTHER_PAGES.find(({ item }) => isItemActive(item, pathname)) ?? null;
}
